import {
  processServiceQualification,
  type ServiceQualificationResult,
  type ServiceQualificationResponse,
} from '@/services/apis/getServiceQualification/schemas';
import { match } from 'ts-pattern';
import { getGlobalStore } from './store';
import type { AddressMatch } from '@/services/apis/addressSearch/schemas';
import type { QueryClient } from '@tanstack/react-query';
import { serviceQualificationQueryOptions } from '@/services/apis/queryOptions/serviceQualificationQueryOptions';
import { parseLocalStorageSelectedAddress } from './local-storage';
import type { FeedbackCardVisible } from '@/stores/store/address-slice';

const getSQResponse = (
  result: ServiceQualificationResult
): FeedbackCardVisible =>
  match(result)
    .with({ type: 'unserviceable' }, () => 'bad-news')
    .with({ type: 'serviceable' }, () => 'great-news')
    .with({ type: 'superloop' }, () => 'visit-superloop')
    .otherwise(() => 'bad-news') as FeedbackCardVisible;

const processSQSearchSuccess = (
  data: ServiceQualificationResponse,
  selectedAddress: AddressMatch
) => {
  const {
    setFeedbackCardAddressSearchVisible,
    setFeedbackCardRecentSearchVisible,
    setLatestSelectedAddress,
  } = getGlobalStore();
  setLatestSelectedAddress(selectedAddress);
  const parsedResult = processServiceQualification(data.sqSummary);
  const sqResponse = getSQResponse(parsedResult);
  setFeedbackCardAddressSearchVisible(sqResponse);
  setFeedbackCardRecentSearchVisible(sqResponse);
};

export const processSQSearch = async (queryClient: QueryClient) => {
  const {
    setSearchAddress,
    setFeedbackCardAddressSearchVisible,
    setFeedbackCardRecentSearchVisible,
    setLatestSelectedAddress,
  } = getGlobalStore();
  const latestSelectedAddress =
    getGlobalStore().addressStore.latestSelectedAddresses;
  const selectedAddress = parseLocalStorageSelectedAddress();
  if (!selectedAddress) {
    return;
  }

  if (latestSelectedAddress.length > 0) {
    return;
  }
  try {
    setSearchAddress(selectedAddress.formattedAddress);
    setLatestSelectedAddress(selectedAddress);
    const data = await queryClient.ensureQueryData(
      serviceQualificationQueryOptions({ isEnabled: true, selectedAddress })
    );

    processSQSearchSuccess(data, selectedAddress);
  } catch {
    setFeedbackCardAddressSearchVisible('bad-news');
    setFeedbackCardRecentSearchVisible('bad-news');
  }
};
